import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, MapPin } from "lucide-react";
import {
  getProductSearchRadius,
  updateProductSearchRadius,
  type ProductSearchRadiusConfig,
} from "../../services/AppConfigService";

const MIN_MILES = 5;
const MAX_MILES = 200;

const FIELDS: { key: keyof ProductSearchRadiusConfig; label: string; hint: string }[] = [
  { key: "walmartMiles", label: "Walmart", hint: "Walmart App provider. Tempo ignores radius." },
  { key: "homeDepotMiles", label: "Home Depot", hint: "Local (non-nationwide) search." },
];

const inRange = (miles: number) => Number.isInteger(miles) && miles >= MIN_MILES && miles <= MAX_MILES;

export default function ProductSearchRadiusSettings() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<ProductSearchRadiusConfig | null>(null);
  const [saved, setSaved] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["product-search-radius"],
    queryFn: getProductSearchRadius,
  });

  useEffect(() => {
    if (data && draft === null) setDraft(data);
  }, [data, draft]);

  const mutation = useMutation({
    mutationFn: updateProductSearchRadius,
    onSuccess: (next) => {
      queryClient.setQueryData(["product-search-radius"], next);
      setDraft(next);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  if (isLoading || !draft) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  const dirty = !data || draft.walmartMiles !== data.walmartMiles || draft.homeDepotMiles !== data.homeDepotMiles;
  const valid = inRange(draft.walmartMiles) && inRange(draft.homeDepotMiles);

  return (
    <div className="max-w-2xl mt-8">
      <div className="flex items-center gap-2 mb-1">
        <MapPin className="w-4 h-4 text-gray-500 dark:text-dark-400" />
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Nearby Store Radius</h2>
      </div>
      <p className="text-xs text-gray-500 dark:text-dark-400 mb-4">
        How far our own product search looks for stores around the searched ZIP. Applies to every user's search; still
        capped at the 25 closest stores. Changing it starts a fresh cache for that radius.
      </p>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {FIELDS.map(({ key, label, hint }) => (
          <label
            key={key}
            className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 dark:border-dark-700 dark:bg-dark-800"
          >
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-gray-900 dark:text-white">{label}</span>
              <span className="block text-[11px] text-gray-500 dark:text-dark-400">{hint}</span>
            </span>
            <span className="flex items-center gap-1 text-xs font-semibold text-gray-600 dark:text-dark-300">
              <input
                type="number"
                min={MIN_MILES}
                max={MAX_MILES}
                step="1"
                aria-label={`${label} search radius in miles`}
                value={Number.isNaN(draft[key]) ? "" : draft[key]}
                onChange={(e) => setDraft({ ...draft, [key]: Math.round(Number(e.target.value)) })}
                className={`w-20 rounded-md border bg-white px-2 py-1 text-right font-mono text-xs text-gray-900 dark:bg-dark-800 dark:text-white ${
                  inRange(draft[key]) ? "border-gray-200 dark:border-dark-600" : "border-amber-500"
                }`}
              />
              <span>mi</span>
            </span>
          </label>
        ))}
      </div>

      {!valid && (
        <p className="mt-2 text-xs font-medium text-amber-600 dark:text-amber-400">
          Radius must be a whole number between {MIN_MILES} and {MAX_MILES} miles.
        </p>
      )}

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={() => mutation.mutate(draft)}
          disabled={!dirty || !valid || mutation.isPending}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {mutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : saved ? <Check className="w-3.5 h-3.5" /> : null}
          {saved ? "Saved" : "Save Radius"}
        </button>
        {mutation.isError && <span className="text-xs text-red-600 dark:text-red-400">Failed to save</span>}
      </div>
    </div>
  );
}
